import { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  ListItem,
  ListItemIcon,
  MenuItem,
  Typography
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import CheckIcon from '@mui/icons-material/Check';
import { useNavigate, useLocation } from 'react-router';

import { navMenuStyles } from './NavMenu';
import AuthUserStore from '@/common/utils/AuthUser';
import AuthModal, { authModalStyles } from '@/RestoreComponent/AuthModal';
import { LoginSelectStores } from '@/common/Selectors';
import PinInputModal from '@/RestoreComponent/PinInputModal';
import { Stack } from '@mui/material';
import { PATH_APP } from '@/routes/paths';
import { useGetFeatureFlags } from '@/views/Auth/hooks';

const useStyles: any = makeStyles((theme: any) => ({
  code: {
    fontSize: '12px',
    fontWeight: 500,
    letterSpacing: '0.3px'
  },
  viewAll: {
    height: '32px'
  },
  viewAllButton: {
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '10px',
    paddingLeft: '0px',
    color: theme.palette.primary.restoreBlue,
    '&:hover': {
      color: theme.palette.primary.quiet,
      backgroundColor: 'transparent'
    }
  }
}));

type ChangeStudioProps = {
  stores: {
    description: string;
    roleName: string;
    storeCode: string;
    id: number;
  }[];
};

const ChangeStudio: React.FC<ChangeStudioProps> = ({ stores }) => {
  const classes = useStyles();
  const navMenuClasses = navMenuStyles();
  const authModalClasses = authModalStyles();
  const currentStoreId = AuthUserStore.getStoreId();

  const navigate = useNavigate();
  const location = useLocation();
  const featureFlags = useGetFeatureFlags();

  const displayedStores = stores.slice(0, 3);

  const [isViewAllOpen, setIsViewAllOpen] = useState(false);
  const [isPinInputOpen, setIsPinInputOpen] = useState(false);

  const [selectedStoreId, setSelectedStoreId] = useState(null);

  const handleViewAllClose = () => {
    setIsViewAllOpen(false);
  };

  const handleSelectStudio = (id: number) => {
    if (id !== currentStoreId) {
      setSelectedStoreId(id);
      setIsViewAllOpen(false);
      setIsPinInputOpen(true);
    }
  };

  const handlePinClose = () => {
    setSelectedStoreId('');
    setIsPinInputOpen(false);
  };

  const handlePinSubmit = async () => {
    setIsPinInputOpen(false);
    await featureFlags.get();

    const appointmentPath = PATH_APP.appointment.root;
    if (location.pathname === appointmentPath) {
      window.location.reload();
    } else {
      navigate(appointmentPath);
    }
  };

  if (stores.length > 1) {
    return (
      <>
        {displayedStores.map(store => {
          return (
            <MenuItem
              key={store.id}
              className={`${navMenuClasses.item} tall`}
              onClick={() => handleSelectStudio(store.id)}
            >
              <Box
                className={classes.box}
                display="flex"
                flexDirection="column"
              >
                {store.description}
                <Typography className={classes.code}>
                  {store.storeCode}
                </Typography>
              </Box>
              {store.id === currentStoreId && (
                <ListItemIcon className={navMenuClasses.endAdornment}>
                  <CheckIcon />
                </ListItemIcon>
              )}
            </MenuItem>
          );
        })}
        {stores.length > 3 && (
          <ListItem className={classes.viewAll}>
            <Button
              className={classes.viewAllButton}
              variant="text"
              onClick={() => setIsViewAllOpen(true)}
              disableRipple
            >
              View all Studios
            </Button>
          </ListItem>
        )}
        <Divider className={classes.divider} />

        {/* View All Studios */}
        <AuthModal isOpen={isViewAllOpen} onClose={handleViewAllClose}>
          <Box className={classes.studioSelect}>
            <LoginSelectStores onChange={handleSelectStudio} data={stores} />
          </Box>
          <Stack className={authModalClasses.buttonContainer}>
            <Button
              fullWidth
              variant="contained"
              className={authModalClasses.cancelButton}
              onClick={handleViewAllClose}
            >
              Cancel
            </Button>
          </Stack>
        </AuthModal>

        {/* Pin Input */}
        <PinInputModal
          isOpen={isPinInputOpen}
          onClose={handlePinClose}
          headerText="Enter your PIN to switch Studios"
          newStudioId={selectedStoreId}
          onPinSubmit={handlePinSubmit}
        />
      </>
    );
  } else {
    return null;
  }
};

export default ChangeStudio;
